import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'universal-cookie';
import { Auth_URL } from '../Axios/config';

const cookies = new Cookies();

const LOGIN_ROUTE = '/Login'; // change to '/login' if your router uses lowercase
const PROD_COOKIE_DOMAIN = 'app.aiwell.in';
const PROD_CANONICAL_ORIGIN = 'https://www.app.aiwell.in';

const isBrowser = typeof window !== 'undefined';

const isProdAppDomain = () => {
    if (!isBrowser) return false;
    return window.location.hostname === 'app.aiwell.in' || window.location.hostname === 'www.app.aiwell.in';
};

const getCookieOptions = () => {
    const baseOptions = { path: '/' };

    if (isProdAppDomain()) {
        return {
            ...baseOptions,
            domain: PROD_COOKIE_DOMAIN,
            secure: true,
            sameSite: 'lax',
        };
    }

    return baseOptions;
};

const expireCookieManually = (name, path = '/', domain) => {
    if (!isBrowser || !name) return;

    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;

    if (domain) {
        cookieString += ` domain=${domain};`;
    }

    cookieString += ' SameSite=Lax;';

    if (window.location.protocol === 'https:') {
        cookieString += ' Secure;';
    }

    document.cookie = cookieString;
};

const removeCookieEverywhere = (name) => {
    if (!name) return;

    const options = getCookieOptions();

    // universal-cookie removals
    cookies.remove(name, options);
    cookies.remove(name, { path: '/' });
    cookies.remove(name);

    if (isProdAppDomain()) {
        cookies.remove(name, { path: '/', domain: PROD_COOKIE_DOMAIN });
        cookies.remove(name, { path: '/', domain: '.app.aiwell.in' });
        cookies.remove(name, { path: '/', domain: 'www.app.aiwell.in' });
    }

    // manual browser cookie expiry for old/mismatched cookies
    expireCookieManually(name, '/');
    expireCookieManually(name, '/', PROD_COOKIE_DOMAIN);
    expireCookieManually(name, '/', '.app.aiwell.in');
    expireCookieManually(name, '/', 'www.app.aiwell.in');
};

export const setTokens = (accessToken, refreshToken) => {
    const options = getCookieOptions();

    cookies.set('accessToken', accessToken, options);
    cookies.set('refreshToken', refreshToken, options);

    // keep localStorage temporarily for backward compatibility
    localStorage.setItem('refreshToken', refreshToken);
};

export const saveUserDetails = (firstname, lastname, email, phone, UID) => {
    cookies.set(
        'UserDetails',
        JSON.stringify({
            firstname,
            lastname,
            email,
            phone,
            UID,
        }),
        getCookieOptions()
    );
};

export const getUserAcc = () => {
    try {
        const data = cookies.get('UserDetails');

        if (!data) return null;

        if (typeof data === 'string') {
            return JSON.parse(data);
        }

        return data;
    } catch (error) {
        console.log('getUserAcc: Failed to retrieve or parse UserDetails cookie:', error);
        return null;
    }
};

export const clearTokens = () => {
    removeCookieEverywhere('accessToken');
    removeCookieEverywhere('refreshToken');
    removeCookieEverywhere('UserDetails');

    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
};

export const logout = (navigate) => {
    clearTokens();

    // wipe current-origin app state
    localStorage.clear();
    sessionStorage.clear();

    if (typeof navigate === 'function') {
        navigate(LOGIN_ROUTE, { replace: true });
        return;
    }

    if (isProdAppDomain()) {
        window.location.replace(`${PROD_CANONICAL_ORIGIN}${LOGIN_ROUTE}`);
        return;
    }

    window.location.replace(LOGIN_ROUTE);
};

export const getAccessToken = () => {
    return cookies.get('accessToken');
};

export const getRefreshToken = () => {
    return cookies.get('refreshToken') || localStorage.getItem('refreshToken');
};

export const getUserDetails = () => {
    try {
        const token = getAccessToken();
        if (!token) return null;

        const extractedToken = jwtDecode(token);

        return {
            UID: extractedToken?.username,
        };
    } catch (error) {
        console.log('getUserDetails: Failed to decode token:', error);
        return null;
    }
};

export const isTokenExpired = (token) => {
    try {
        if (!token) return true;

        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;

        return decoded.exp < now;
    } catch (error) {
        console.log('isTokenExpired: Failed to decode token:', error);
        return true;
    }
};

export const refreshToken = async () => {
    const refreshTokenValue = getRefreshToken();

    if (!refreshTokenValue) {
        clearTokens();
        return false;
    }

    const userDetails = getUserDetails();
    const userAcc = getUserAcc();
    const dynamicUid = userDetails?.UID || userAcc?.UID || '';

    console.log("refreshToken: dynamicUid resolved to:", dynamicUid);

    try {
        const response = await axios.post(
            `${Auth_URL}Renew`,
            {
                user: dynamicUid,
                token: refreshTokenValue,
            }
        );

        setTokens(response.data.accessToken, response.data.refreshToken);
        return true;
    } catch (error) {
        console.log('refreshToken: Failed to refresh token:', error);
        clearTokens();
        return false;
    }
};